import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';
import { Row, Select } from 'antd';
import _ from 'lodash';

import { DownOutlined } from '@ant-design/icons';
import { connect } from '../../../../../../../../StateProvider';

import routes from '../../../../../../../../../routes';
import NavRoute from '../../../../../../../../common/router/Route';
import StateRedirect from '../../../../../../../../common/router/StateRedirect';
import getLink from '../../../../../../../../common/router/getLink';

import userSettingsActions from '../../../../../../../../../actions/userSettingsActions';
import ViewActions from '../ViewActions';
import ViewStoreActions from '../../../../../../../../../actions/viewActions';

import styles from './viewSelector.less';

const defaultViewsMap = {
  allRecords: '0',
  newView: '$new',
};

const ViewSelector = (props) => {
  const onSelect = (viewId, option) => {
    const { catalogId, sceneId, history, location, isNewViewExist } = props;

    userSettingsActions.setOption({
      catalogId,
      option: 'defaultViewId',
      value: viewId,
    });

    /**
     * @see кейс - мы хотим создать новый вид, но позже передумали и выбрали другой вид.
     * Возникает следующая ситуация: после выбора всех записей старый вид с айди "$new" остался в сторе и пропс isNewViewExist
     * думает, что сейчас мы хотим создать новый вид, хотя по факту мы выбрали СУЩЕСТВУЮЩИЙ старый вид и нас редиректит на $new.
     * Поэтому при селекте мы просто спокойно убираем "отмененный" == "$new" вид из стора и нас не перекидывает.
     */
    if (isNewViewExist) {
      const paramsToDeleteNewView = {
        catalogId,
        sceneId: sceneId && sceneId,
        viewId: defaultViewsMap.newView,
      };

      ViewStoreActions.truncateView(paramsToDeleteNewView);
    }

    history.push(getLink(location, '', { viewId }));
  };

  const views = useMemo(() => {
    let viewsList = (props.views && props.views.valueSeq()) || new Immutable.Seq();

    if (viewsList && viewsList.size > 1) {
      // sort by name keeping 0 view first
      const allRecordsView = viewsList.find((v) => v.get('id') === defaultViewsMap.allRecords);
      const newView = viewsList.find((v) => v.get('id') === defaultViewsMap.newView);

      viewsList = viewsList.filter(
        (v) => v.get('id') !== defaultViewsMap.allRecords && v.get('id') !== defaultViewsMap.newView,
      );
      viewsList = viewsList.filter((c) => !!c.get('displayName')).sortBy((c) => c.get('displayName'));
      viewsList = allRecordsView ? [allRecordsView, ...viewsList] : viewsList;
      viewsList = newView ? [...viewsList, newView] : viewsList;
      viewsList = Immutable.fromJS(viewsList);
    }

    return viewsList;
  }, [props.views]);

  const defaultView = useMemo(() => {
    let { defaultViewId, match } = props;

    defaultViewId = defaultViewId && defaultViewId.get('value');
    const matchViewId = _.get(match, ['params', 'viewId']);

    if (matchViewId) {
      return Immutable.Map({ id: matchViewId });
    }

    if (defaultViewId) {
      return Immutable.Map({ id: defaultViewId });
    }

    return Immutable.Map({ id: defaultViewsMap.allRecords });
  }, [props.defaultViewId, props.match]);

  return (
    <Row className={cn(styles.viewsSelectorContainer, props.className)}>
      {/* редирект на новый вид */}
      {props.isNewViewExist && !(props.viewId === defaultViewsMap.newView) ? (
        <StateRedirect route={routes.view} params={{ viewId: defaultViewsMap.newView }} />
      ) : null}

      {/* редирект на вид по умолчанию, если выбран несуществующий вид */}
      <NavRoute route={routes.view}>
        {({ match }) => {
          if (
            props.viewsLoaded &&
            props.views &&
            !props.views.find((view) => view.get('id') === _.get(match, ['params', 'viewId']))
          ) {
            return (
              <StateRedirect
                route={routes.view}
                params={{
                  viewId: defaultView && defaultView.get('id'),
                }}
              />
            );
          }
          return null;
        }}
      </NavRoute>

      <NavRoute route={routes.view}>
        {({ match }) => {
          const viewId = _.get(match, ['params', 'viewId']);
          return (
            <Select
              value={viewId}
              defaultValue={viewId}
              className={styles.viewsSelector}
              showSearch
              suffixIcon={<DownOutlined />}
              onSelect={onSelect}
            >
              {views
                .map((view) => (
                  <Select.Option key={view.get('id')} title={view.get('displayName')}>
                    {view.get('displayName')}
                  </Select.Option>
                ))
                .toJS()}
            </Select>
          );
        }}
      </NavRoute>

      <ViewActions
        sceneId={props.sceneId}
        catalogId={props.catalogId}
        viewId={props.viewId}
        catalog={props.catalog}
        className={styles.viewActions}
      />
    </Row>
  );
};

ViewSelector.propTypes = {
  sceneId: PropTypes.string.isRequired,
  catalogId: PropTypes.string.isRequired,
  viewId: PropTypes.string.isRequired,
  views: ImmutablePropTypes.map.isRequired,
};

export default withTranslation()(
  connect(
    withRouter(ViewSelector),
    {
      userSettings: ['userSettings', 'catalogs'],
      scenes: ['scenes'],
    },
    (props, { userSettings, scenes }) => {
      const scene = scenes.get(props.sceneId);
      const { catalog } = props;
      const { viewId } = props;
      const views = catalog && catalog.get('views') && catalog.get('views').merge(scene && scene.get('views'));
      const viewsLoaded = catalog && catalog.get('viewsLoaded');

      const filtersChanged = (scene && scene.getIn(['views', viewId, 'filtersChanged'])) || false;

      const defaultViewId = userSettings.getIn([props.catalogId, 'options', 'defaultViewId']);

      const isNewViewExist = views && views.some((view) => view.get('id') === defaultViewsMap.newView);

      return {
        scene,
        views,
        viewsLoaded,
        filtersChanged,
        isNewViewExist,
        defaultViewId,
        ...props,
      };
    },
  ),
);
